import {
  AccessLevels,
  Feature,
  Features,
  hasAccessLevelOrMore
} from '@awork/features/workspace/models/permissions.model'
import { Document, DocumentSection } from '@awork/features/document/models/document.model'
import type { Signal } from '@angular/core'
import { Injectable, computed, inject } from '@angular/core'
import type { SelectOptions } from '@awork/core/state/signal-store/types'
import { Order } from '@awork/core/state/signal-store/types'
import {
  checkPermissions,
  checkProjectRolePermissions,
  hasProjectOwnTeamPermissions
} from '@awork/features/workspace/services/permission-service/permissions.helpers'

import { DocumentSpaceQuery } from './document-space.query'
import { DocumentStore } from '@awork/features/document/state/document.store'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'
import { FeatureId } from '@awork/_shared/models/subscription-plan.model'
import { PermissionsQuery } from '@awork/features/workspace/state/permissions.query'
import { ProjectQuery } from '@awork/features/project/state/project.query'
import { SettingsQuery } from '@awork/framework/state/settings.query'
import { SubscriptionPlanQuery } from '@awork/_shared/state/subscription-plan.query'
import { TeamQuery } from '@awork/features/team/state/team.query'
import type { User } from '@awork/features/user/models/user.model'
import { UserQuery } from '@awork/features/user/state/user.query'
import { SubscriptionQuery } from '@awork/_shared/state/subscription.query'

interface QueryOptions {
  searchQuery?: string
  nested?: boolean
  depth?: number
  section?: DocumentSection
}

@Injectable({ providedIn: 'root' })
export class DocumentQuery extends EntitySignalQuery<Document> {
  protected store: DocumentStore

  private settingsQuery = inject(SettingsQuery)
  private userQuery = inject(UserQuery)
  private documentSpaceQuery = inject(DocumentSpaceQuery)
  private permissionsQuery = inject(PermissionsQuery)
  private projectQuery = inject(ProjectQuery)
  private teamQuery = inject(TeamQuery)
  private subscriptionPlanQuery = inject(SubscriptionPlanQuery)
  private subscriptionQuery = inject(SubscriptionQuery)

  constructor() {
    const store = inject(DocumentStore)

    super(store)

    this.store = store
  }

  /**
   * Gets document by id
   * @param {string} id
   * @returns {Document}
   */
  getDocument(id: string): Document {
    const document = this.getEntity(id)

    return this.mapEntity(document)
  }

  /**
   * Gets a document by id
   * @param {string} id
   * @returns {Signal<Document>}
   */
  queryDocument(id: string): Signal<Document> {
    return this.queryEntity(id)
  }

  /**
   * Gets documents applying the select options
   * @param {SelectOptions<Document> & QueryOptions} options
   * @returns {Signal<Document[]>}
   * @retuns {Signal<Document[]>}
   */
  queryDocuments(options?: SelectOptions<Document> & QueryOptions): Signal<Document[]> {
    return computed(() => {
      const queriedDocuments = this.queryAll({
        filterBy: options?.filterBy || (document => this.searchFilter(document, options?.searchQuery)),
        sortBy: options?.sortBy || 'order',
        sortByOrder: options?.sortByOrder || Order.ASC,
        limitTo: options?.limitTo
      })

      const documents = queriedDocuments().map(document => {
        document.section = options?.section
        return document
      })

      if (options?.nested || options?.depth !== undefined) {
        const isSubset =
          options?.section === DocumentSection.Favorites ||
          options?.section === DocumentSection.Trash ||
          options?.section === DocumentSection.Shared

        const nestedDocuments = this.orderByParent(documents, isSubset)

        return options.depth !== undefined
          ? nestedDocuments.filter(document => document.depth === options.depth)
          : nestedDocuments
      }

      return this.mapEntities(documents)
    })
  }

  /**
   * Gets documents by ids
   * @param {string[]} documentIds
   * @param {SelectOptions<Document> & QueryOptions} options
   * @returns {Signal<Document[]>}
   */
  queryDocumentsByIds(documentIds: string[], options?: SelectOptions<Document> & QueryOptions): Signal<Document[]> {
    return computed(() => {
      if (!documentIds.length) {
        return []
      }

      const documents = this.queryMany(documentIds)

      return documents()
    })
  }

  /**
   * Gets documents by space
   * @param {string} spaceId
   * @param {QueryOptions} queryOptions
   * @returns {Signal<Document[]>}
   */
  querySpaceDocuments(spaceId: string, queryOptions?: QueryOptions): Signal<Document[]> {
    return this.queryDocuments({
      filterBy: document => document.documentSpaceId === spaceId && !document.movedToTrashOn,
      section: DocumentSection.Spaces,
      ...queryOptions
    })
  }

  /**
   * Gets documents by project
   * @param {string} projectId
   * @param {QueryOptions} queryOptions
   * @returns {Signal<Document[]>}
   */
  queryProjectDocuments(projectId: string, queryOptions?: QueryOptions): Signal<Document[]> {
    return this.queryDocuments({
      filterBy: document => document.projectId === projectId && !document.movedToTrashOn,
      section: DocumentSection.Project,
      ...queryOptions
    })
  }

  /**
   * Gets private documents
   * @param {QueryOptions} queryOptions
   * @returns {Signal<Document[]>}
   */
  queryPrivateDocuments(queryOptions?: QueryOptions): Signal<Document[]> {
    const currentUserQuery = this.userQuery.queryCurrentUser()
    const currentUser = currentUserQuery()

    return this.queryDocuments({
      filterBy: document =>
        document.isPrivate && document.rootDocumentCreatedBy === currentUser.id && !document.movedToTrashOn,
      section: DocumentSection.Private,
      ...queryOptions
    })
  }

  /**
   * Gets the first document by space (root document)
   * This is useful to get the first document to show/open in a space
   * @param {string} spaceId
   * @returns {Signal<Document>}
   */
  queryFirstDocumentBySpace(spaceId: string): Signal<Document> {
    return computed(() => {
      const documents = this.queryDocuments({
        filterBy: document => document.documentSpaceId === spaceId && !document.parentId && !document.movedToTrashOn,
        limitTo: 1
      })

      return documents()[0]
    })
  }

  /**
   * Gets the first document by project (root document)
   * This is useful to get the first document to show/open in a project
   * @param {string} projectId
   * @returns {Signal<Document>}
   */
  queryFirstDocumentByProject(projectId: string): Signal<Document> {
    return computed(() => {
      const documents = this.queryDocuments({
        filterBy: document => document.projectId === projectId && !document.parentId && !document.movedToTrashOn,
        limitTo: 1
      })

      return documents()[0]
    })
  }

  /**
   * Gets the descendant documents of multiple documents
   * @param {string[]} documentIds
   * @returns {Signal<Document[]>}
   */
  queryDescendantDocuments(documentIds: string[]): Signal<Document[]> {
    return computed(() => {
      if (!documentIds?.length) {
        return []
      }

      const allDocuments = this.queryAll({ sortBy: 'order', sortByOrder: Order.ASC })

      // Create a map of children by parentId for efficient lookups
      const childrenByParentId = new Map<string, Document[]>()

      allDocuments().forEach(doc => {
        if (doc.parentId) {
          if (!childrenByParentId.has(doc.parentId)) {
            childrenByParentId.set(doc.parentId, [])
          }

          childrenByParentId.get(doc.parentId).push(doc)
        }
      })

      const descendants: Document[] = []

      // Recursive function to collect all descendants, avoiding duplicates
      const collectDescendants = (parentId: string, processed = new Set<string>()) => {
        const children = childrenByParentId.get(parentId) || []

        for (const child of children) {
          if (!processed.has(child.id)) {
            processed.add(child.id)
            descendants.push(child)
            collectDescendants(child.id, processed)
          }
        }
      }

      // Process each document ID in the array
      documentIds.forEach(id => collectDescendants(id))

      return this.mapEntities(descendants)
    })
  }

  /**
   * Gets the ancestor documents of a document
   * @param {string} documentId
   * @returns {Signal<Document[]>}
   */
  queryAncestorDocuments(documentId: string): Signal<Document[]> {
    return computed(() => {
      const document = this.queryEntity(documentId)

      const ancestors: Document[] = []

      let currentDocument = document()

      while (currentDocument?.parentId) {
        const parentDocument = this.queryEntity(currentDocument.parentId)

        if (parentDocument()) {
          ancestors.push(parentDocument())
          currentDocument = parentDocument()
        } else {
          currentDocument = null
        }
      }

      return ancestors.reverse()
    })
  }

  /**
   * Gets the direct children documents of a document.
   * If the document is a root document, it will return the root documents.
   * @param {Partial<{ parentId: string; documentSpaceId: string; projectId: string; isPrivate: boolean; movedToTrash: boolean; }>} filter
   * @returns {Signal<Document[]>}
   */
  queryDocumentsByParent(
    filter: Partial<{
      parentId: string
      documentSpaceId: string
      projectId: string
      isPrivate: boolean
      movedToTrash: boolean
    }>
  ): Signal<Document[]> {
    const currentUserQuery = this.userQuery.queryCurrentUser()
    const currentUser = currentUserQuery()

    return this.queryDocuments({
      filterBy: document =>
        (filter.parentId == null ? document.parentId == null : document.parentId === filter.parentId) &&
        document.documentSpaceId === filter.documentSpaceId &&
        document.projectId === filter.projectId &&
        document.isPrivate === (filter.isPrivate ?? false) &&
        (document.createdBy === currentUser.id || !filter.isPrivate) &&
        (filter.movedToTrash ? !!document.movedToTrashOn : !document.movedToTrashOn)
    })
  }

  /**
   * Gets the favorite documents.
   * The favorite document ids come from the user settings.
   * @param {QueryOptions} queryOptions
   * @returns {Signal<Document[]>}
   */
  queryFavoriteDocuments(queryOptions?: QueryOptions): Signal<Document[]> {
    return computed(() => {
      const settings = this.settingsQuery.query('documents')
      const favoriteDocumentIds = settings()?.favoriteDocuments

      if (!favoriteDocumentIds?.length) {
        return []
      }

      const favoriteDocuments = this.queryDocuments({
        filterBy: document => favoriteDocumentIds.includes(document.id) && !document.movedToTrashOn,
        sortBy: 'name',
        sortByOrder: Order.ASC,
        section: DocumentSection.Favorites
      })

      if (queryOptions?.nested) {
        const queriedDescendantDocuments = this.queryDescendantDocuments(favoriteDocumentIds)

        const rootDocuments = favoriteDocuments().map(document => {
          document.isSubsetRoot = true
          return document
        })

        const descendantDocuments = queriedDescendantDocuments()
          .filter(document => !document.movedToTrashOn)
          .map(document => {
            document.section = DocumentSection.Favorites
            return document
          })

        const nestedDocuments = this.orderByParent([...rootDocuments, ...descendantDocuments])

        return queryOptions.depth !== undefined
          ? nestedDocuments.filter(document => document.depth === queryOptions.depth)
          : nestedDocuments
      }

      return favoriteDocuments()
    })
  }

  /**
   * Gets the shared documents.
   * @param {QueryOptions} queryOptions
   * @returns {Signal<Document[]>}
   */
  querySharedDocuments(queryOptions?: QueryOptions): Signal<Document[]> {
    return computed(() => {
      const currentUser = this.userQuery.queryCurrentUser()

      const documents = this.queryDocuments({
        ...queryOptions,
        filterBy: document => this.isSharedDocument(document, currentUser()) && !document.movedToTrashOn,
        section: DocumentSection.Shared
      })

      return documents()
    })
  }

  /**
   * Gets the deleted space documents.
   * @param {string} spaceId
   * @param {QueryOptions} queryOptions
   * @returns {Signal<Document[]>}
   */
  queryDeletedSpaceDocuments(spaceId: string, queryOptions?: QueryOptions): Signal<Document[]> {
    return computed(() => {
      const documents = this.queryDocuments({
        ...queryOptions,
        filterBy: document => document.documentSpaceId === spaceId && !!document.movedToTrashOn,
        section: DocumentSection.Trash
      })
      return documents()
    })
  }

  /**
   * Gets the deleted project documents.
   * @param {string} projectId
   * @param {QueryOptions} queryOptions
   * @returns {Signal<Document[]>}
   */
  queryDeletedProjectDocuments(projectId: string, queryOptions?: QueryOptions): Signal<Document[]> {
    return computed(() => {
      const documents = this.queryDocuments({
        ...queryOptions,
        filterBy: document => document.projectId === projectId && !!document.movedToTrashOn,
        section: DocumentSection.Trash
      })
      return documents()
    })
  }

  /**
   * Gets the deleted private documents.
   * @param {QueryOptions} queryOptions
   * @returns {Signal<Document[]>}
   */
  queryDeletedPrivateDocuments(queryOptions?: QueryOptions): Signal<Document[]> {
    const currentUserQuery = this.userQuery.queryCurrentUser()
    const currentUser = currentUserQuery()

    return computed(() => {
      const documents = this.queryDocuments({
        ...queryOptions,
        filterBy: document =>
          document.isPrivate && document.rootDocumentCreatedBy === currentUser.id && !!document.movedToTrashOn,
        section: DocumentSection.Trash
      })

      return documents()
    })
  }

  /**
   * Gets the recently opened documents.
   * The recent document ids come from the user settings and are ordered by last opened date.
   * @returns {Signal<Document[]>}
   */
  queryRecentDocuments(): Signal<Document[]> {
    return computed(() => {
      const settings = this.settingsQuery.query('documents')
      const recentDocuments = settings()?.recentDocuments || []

      const documentIds = [...recentDocuments]
        .sort((a, b) => new Date(b.lastOpened).getTime() - new Date(a.lastOpened).getTime())
        .map(doc => doc.id)

      const documents = this.queryMany(documentIds)

      // Filter out documents that have been deleted
      let filteredDocuments = documents().filter(document => !document.movedToTrashOn)

      // Filter out project documents if project docs are disabled
      const isProjectDocsEnabled = this.subscriptionPlanQuery.queryIsPlanFeatureAvailable(FeatureId.ProjectDocuments)

      if (!isProjectDocsEnabled()) {
        filteredDocuments = filteredDocuments.filter(document => !document.projectId)
      }

      return this.mapEntities(filteredDocuments)
    })
  }

  /**
   * Gets the document that has a link to the specified task
   * @param {string} taskId
   * @returns {Signal<Document>}
   */
  queryDocumentByLinkedTask(taskId: string): Signal<Document> {
    return computed(() => {
      const documents = this.queryDocuments({ filterBy: document => document.taskId === taskId })

      if (!documents().length) {
        return undefined
      }

      return documents()[0]
    })
  }

  /**
   * Gets whether or not the user is allowed to read or manage(edit) a document
   * @param {string} documentId
   * @param {AccessLevels} accessLevel
   * @returns {Signal<boolean>}
   */
  queryIsAllowed(documentId: string, accessLevel: AccessLevels): Signal<boolean> {
    // eslint-disable-next-line complexity
    return computed(() => {
      const document = this.queryDocument(documentId)
      const currentUser = this.userQuery.queryCurrentUser()
      const userPermissions = this.permissionsQuery.queryUserPermissions()
      const isFeatureAvailable = this.subscriptionPlanQuery.queryIsPlanFeatureAvailable(FeatureId.Documents)

      if (!document()) {
        return false
      }

      // Connected user should only have acces to project docs in the connected workspace.
      // Hence, a connect user explicitly should not have access to any space docs or private docs
      // in the connected workspace.
      if (!document().projectId && document().isExternal) {
        return false
      }

      // Check if the current user is an admin, the feature is available for the plan, the document is not private and the user is not external
      if (userPermissions().isAdmin && isFeatureAvailable() && !document().isPrivate && !document().isExternal) {
        return true
      }

      // Check if document is private and belongs to current user
      if (document().isPrivate && document().rootDocumentCreatedBy === currentUser().id) {
        return true
      }

      // Check if user has document space access level
      if (document().documentSpaceId) {
        const hasSpaceAccess = this.documentSpaceQuery.queryIsAllowed(document().documentSpaceId, accessLevel)

        if (hasSpaceAccess()) {
          return true
        }
      }

      // Check if user has project document access level through project role or team
      if (document().projectId) {
        const project = this.projectQuery.queryProject(document().projectId)
        const isProjectDocsEnabled = this.subscriptionPlanQuery.queryIsPlanFeatureAvailable(FeatureId.ProjectDocuments)

        if (
          userPermissions().userId &&
          project()?.createdBy === userPermissions().userId &&
          !project()?.isExternal &&
          isProjectDocsEnabled()
        ) {
          return true
        }

        // Project Docs Role Permissions
        const hasProjectDocsRoleAccess = checkProjectRolePermissions(
          userPermissions(),
          [Features.ProjectDocumentData],
          accessLevel,
          false,
          project()
        )

        // A non- external admin always has access to non- private projects docs
        const hasProjectDocsAccess =
          hasProjectDocsRoleAccess || (userPermissions()?.isAdmin && !project()?.isExternal && !project()?.isPrivate)

        if (hasProjectDocsAccess) {
          return true
        }

        // Global Project Docs Permissions
        const hasProjectDocsGlobalAccess = checkPermissions(
          userPermissions().permissions,
          [Features.ProjectDocumentData],
          accessLevel,
          false
        )
        const hasOwnTeamsRestriction = userPermissions().permissions.find(
          permission => permission.feature === Feature.getFeatureStringByEnum(Features.ProjectOwnTeam)
        )
        const ownTeams = this.teamQuery.getUserTeams(userPermissions().userId)
        const isProjectTeamMember = hasProjectOwnTeamPermissions(project(), ownTeams, userPermissions())

        if (hasOwnTeamsRestriction && isProjectTeamMember && hasProjectDocsGlobalAccess) {
          return true
        } else if (hasProjectDocsGlobalAccess && !hasOwnTeamsRestriction) {
          return true
        }
      }

      // Check if document has inherited workspace access from parent document
      if (document().inheritedWorkspaceAccessFromDocument) {
        const inheritedWorkspaceAccess = document().inheritedWorkspaceAccessFromDocument.workspaceAccessLevel

        if (hasAccessLevelOrMore(inheritedWorkspaceAccess, accessLevel) && !document().isExternal) {
          return true
        }
      }

      // Check if document has workspace access level
      if (document().workspaceAccessLevel) {
        const hasWorkspaceLevelAccess = hasAccessLevelOrMore(document().workspaceAccessLevel, accessLevel)

        if (hasWorkspaceLevelAccess && !document().isExternal) {
          return true
        }
      }

      // Check if user is in a team that has access
      const documentTeams = document().teams.filter(teamAccess => {
        const team = this.teamQuery.queryTeam(teamAccess.teamId)

        const teamUserIds = team()?.userIds || []

        return teamUserIds.some(userId => userId === currentUser().id)
      })

      if (documentTeams.length) {
        const hasTeamAccess = documentTeams.some(documentTeam => {
          const isInherited = documentTeam.inheritedDocumentToTeam

          return isInherited
            ? hasAccessLevelOrMore(documentTeam.inheritedDocumentToTeam.accessLevel, accessLevel)
            : hasAccessLevelOrMore(documentTeam.accessLevel, accessLevel)
        })

        if (hasTeamAccess) {
          return true
        }
      }

      // Check if user is a contributor
      const documentContributor = document().contributors.find(contributor => contributor.userId === currentUser().id)

      if (documentContributor) {
        const isInherited = documentContributor.inheritedDocumentContributor
        const hasContributorAccess = isInherited
          ? hasAccessLevelOrMore(documentContributor.inheritedDocumentContributor.accessLevel, accessLevel)
          : hasAccessLevelOrMore(documentContributor.accessLevel, accessLevel)

        if (hasContributorAccess) {
          return true
        }
      }

      return false
    })
  }

  /**
   * Checks if the document is shared with the current user
   * @param {Document} document
   * @param {User} currentUser
   * @returns {boolean}
   */
  private isSharedDocument(document: Document, currentUser: User): boolean {
    // Check if document is private
    if (document.isPrivate && document.rootDocumentCreatedBy === currentUser.id) {
      return false
    }

    // Check document access level
    if (
      (document.workspaceAccessLevel === AccessLevels.Read || document.workspaceAccessLevel === AccessLevels.Manage) &&
      !document.isExternal
    ) {
      return true
    }

    // Check inherited workspace access level
    if (
      !document.isExternal &&
      !document.workspaceAccessLevel &&
      (document.inheritedWorkspaceAccessFromDocument?.workspaceAccessLevel === AccessLevels.Read ||
        document.inheritedWorkspaceAccessFromDocument?.workspaceAccessLevel === AccessLevels.Manage)
    ) {
      return true
    }

    // Check document teams against user teams
    const userIsInDocumentTeam = document.teams.some(documentTeam => {
      return currentUser.teams.map(userTeam => userTeam.id).includes(documentTeam.teamId)
    })

    if (userIsInDocumentTeam) {
      return true
    }

    // Check document contributors
    return document.contributors.some(contributor => contributor.userId === currentUser.id)
  }

  /**
   * Filter used for search by name
   * @param {Document} document
   * @param {string} searchQuery
   * @returns {boolean}
   */
  private searchFilter(document: Document, searchQuery: string): boolean {
    return !searchQuery || document.name?.toLowerCase().includes(searchQuery?.toLowerCase())
  }

  /**
   * Orders documents by parent.
   * The child documents are next to their parent document.
   * @param {Document[]} documents
   * @param {boolean} includeOrphans If true, orphan documents will be added as root documents and then sorted by name
   * @returns {Document[]}
   */
  private orderByParent(documents: Document[], includeOrphans?: boolean): Document[] {
    const MAX_DEPTH = 10

    // Create a map of children by parentId and collect root documents in a single pass
    const childrenByParentId = new Map<string, Document[]>()
    const processedDocumentIds = new Set<string>()
    const rootDocuments: Document[] = []

    for (const document of documents) {
      if (document.parentId && !document.isSubsetRoot) {
        if (!childrenByParentId.has(document.parentId)) {
          childrenByParentId.set(document.parentId, [])
        }

        // Avoid duplicates in children
        const children = childrenByParentId.get(document.parentId)

        if (!children.some(child => child.id === document.id)) {
          children.push(document)
          processedDocumentIds.add(document.id)
        }
      } else {
        // It's a root document
        rootDocuments.push(new Document({ ...document, depth: 0 }))
        processedDocumentIds.add(document.id)
      }
    }

    // Add orphan documents as subset root documents
    if (includeOrphans) {
      // Get documents which parents are not processed (not present)
      for (const parents of childrenByParentId.keys()) {
        if (!processedDocumentIds.has(parents)) {
          const orphanDocuments = childrenByParentId.get(parents)

          rootDocuments.push(
            ...orphanDocuments.map(orphanDocument => new Document({ ...orphanDocument, depth: 0, isSubsetRoot: true }))
          )
        }
      }

      // Sort the root documents by name, untitled documents will be at the end
      rootDocuments.sort((a, b) => {
        if (a.name && b.name) {
          return a.name.localeCompare(b.name)
        } else if (a.name) {
          return -1
        } else if (b.name) {
          return 1
        }

        return 0
      })
    }

    const result: Document[] = []

    // Recursive function to process documents
    const addDocumentAndDescendants = (documents: Document[], currentDepth = 0) => {
      for (let i = 0; i < documents.length; i++) {
        const document = documents[i]

        result.push(
          new Document({
            ...document,
            depth: currentDepth,
            hasChildren: childrenByParentId.has(document.id),
            childrenIds: childrenByParentId.get(document.id)?.map(child => child.id),
            isFirstChild: i === 0,
            isLastChild: i === documents.length - 1
          })
        )

        const children = childrenByParentId.get(document.id)

        if (children?.length && currentDepth < MAX_DEPTH) {
          addDocumentAndDescendants(children, currentDepth + 1)
        }
      }
    }

    addDocumentAndDescendants(rootDocuments)

    return result
  }
}
